// US-DEPOSIT-01 -- guardian records a deposit for a child.
//
// Amount is collected in the family currency, or in a foreign currency for an
// explicit overseas gift, and converted to minor units before sending. Source is
// a fixed enum per the schema seed. Note is optional.

import { type AppError, type DepositCreateResult, depositCreate } from '@chorepop/core/client';
import { SUPPORTED_MONEY_CURRENCIES } from '@chorepop/core/domain';
import { type Component, For, Show, createSignal } from 'solid-js';
import { type CloudFxRate, fetchDailyFxRate } from '../cloud/cloudAuth';
import { ActionScreen } from '../components/ActionScreen';
import { AmountPad } from '../components/AmountPad';

type Props = {
  sessionId: string;
  childAccountId: number;
  childDisplayName: string;
  currencyCode: string;
  onDeposited: (result: DepositCreateResult) => void;
  onCancel: () => void;
};

const SOURCES = [
  { code: 'chore', label: 'Chore reward' },
  { code: 'allowance', label: 'Allowance' },
  { code: 'gift', label: 'Family gift' },
  { code: 'found', label: 'Found money' },
  { code: 'other', label: 'Other' },
];

export const RecordDepositScreen: Component<Props> = (props) => {
  const [amountInput, setAmountInput] = createSignal('');
  const [sourceCode, setSourceCode] = createSignal('allowance');
  const [depositCurrency, setDepositCurrency] = createSignal(props.currencyCode);
  const [fxRate, setFxRate] = createSignal<CloudFxRate | null>(null);
  const [loadingRate, setLoadingRate] = createSignal(false);
  const [note, setNote] = createSignal('');
  const [submitting, setSubmitting] = createSignal(false);
  const [errorMessage, setErrorMessage] = createSignal<string | null>(null);
  const parseAmountMinorUnits = (): number | null => {
    const raw = amountInput().trim();
    if (raw === '') return null;
    // Allow "12", "12.5", "12.50", "0.25". Reject anything else.
    if (!/^\d+(\.\d{1,2})?$/.test(raw)) return null;
    const major = Number.parseFloat(raw);
    if (!Number.isFinite(major) || major <= 0) return null;
    // Convert to minor units. Multiply by 100 and round to handle
    // floating-point imprecision like 0.1 * 100 = 10.000000000000002.
    return Math.round(major * 100);
  };

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    setErrorMessage(null);

    const minorUnits = parseAmountMinorUnits();
    if (minorUnits === null) {
      setErrorMessage('Enter an amount greater than zero (e.g. 5 or 12.50).');
      return;
    }
    const foreign = depositCurrency() !== props.currencyCode;
    const currentFx = fxRate();
    if (foreign && !currentFx) {
      setErrorMessage('Wait for the latest daily exchange rate, or use the family currency.');
      return;
    }

    setSubmitting(true);
    try {
      const trimmedNote = note().trim();
      const result = await depositCreate({
        sessionId: props.sessionId,
        childAccountId: props.childAccountId,
        amountMinorUnits: minorUnits,
        sourceCode: sourceCode(),
        ...(trimmedNote === '' ? {} : { note: trimmedNote }),
        ...(foreign && currentFx
          ? { fx: { currencyCode: depositCurrency(), ratePpm: currentFx.ratePpm } }
          : {}),
      });
      if (!result.ok) {
        setErrorMessage(humanReadableError(result.error));
        return;
      }
      setAmountInput('');
      setNote('');
      props.onDeposited(result.value);
    } finally {
      setSubmitting(false);
    }
  };

  const changeCurrency = async (currency: string) => {
    setDepositCurrency(currency);
    setFxRate(null);
    setErrorMessage(null);
    if (currency === props.currencyCode) return;
    setSourceCode('gift');
    setLoadingRate(true);
    try {
      setFxRate(await fetchDailyFxRate(currency, props.currencyCode === 'USD' ? 'USD' : 'GBP'));
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Could not load the daily rate.');
    } finally {
      setLoadingRate(false);
    }
  };

  return (
    <ActionScreen
      eyebrow="Posts immediately — no approval"
      title="Record a deposit"
      onBack={props.onCancel}
      footer={
        <button
          type="submit"
          form="record-deposit-form"
          class="primary action-screen-primary"
          disabled={submitting()}
        >
          {submitting() ? 'Recording…' : `Record for ${props.childDisplayName}`}
        </button>
      }
    >
      <section class="action-callout action-callout--warm">
        <span aria-hidden="true">+</span>
        <p>
          This money posts straight away and follows {props.childDisplayName}'s usual bucket split.
        </p>
      </section>
      <form id="record-deposit-form" onSubmit={handleSubmit} novalidate class="action-form">
        <AmountPad
          id="record-deposit-amount"
          label="Amount"
          value={amountInput()}
          currencyCode={depositCurrency()}
          disabled={submitting()}
          onChange={(value) => {
            setAmountInput(value);
            setErrorMessage(null);
          }}
        />

        <label class="action-note-field" for="record-deposit-currency">
          <span>Currency</span>
          <select
            id="record-deposit-currency"
            value={depositCurrency()}
            disabled={submitting()}
            onChange={(event) => void changeCurrency(event.currentTarget.value)}
          >
            <option value={props.currencyCode}>Family currency ({props.currencyCode})</option>
            <For
              each={SUPPORTED_MONEY_CURRENCIES.filter(
                (currency) => currency.code !== props.currencyCode,
              )}
            >
              {(currency) => (
                <option value={currency.code}>Overseas gift — {currency.label}</option>
              )}
            </For>
          </select>
        </label>
        <Show when={depositCurrency() !== props.currencyCode}>
          <p class="field-hint" aria-live="polite">
            <Show when={!loadingRate()} fallback="Loading the daily exchange rate…">
              <Show when={fxRate()} fallback="A daily exchange rate is required.">
                {(rate) =>
                  `The gift will be converted to ${props.currencyCode} using the ${rate().effectiveDate} daily rate. Source: ECB statistics via Frankfurter; ChorePop calculates the family-currency amount.`
                }
              </Show>
            </Show>
          </p>
        </Show>

        <section class="action-choice-section" aria-labelledby="record-deposit-source-label">
          <span id="record-deposit-source-label" class="action-field-label">
            Where's it from?
          </span>
          <div class="action-chip-row">
            <For each={SOURCES}>
              {(source) => (
                <button
                  type="button"
                  class={`action-chip${sourceCode() === source.code ? ' is-selected' : ''}`}
                  aria-pressed={sourceCode() === source.code}
                  disabled={submitting()}
                  onClick={() => setSourceCode(source.code)}
                >
                  {source.label}
                </button>
              )}
            </For>
          </div>
        </section>

        <label class="action-note-field">
          <span>
            Note <em>Optional</em>
          </span>
          <input
            type="text"
            autocomplete="off"
            disabled={submitting()}
            placeholder="What was this for?"
            value={note()}
            onInput={(e) => setNote(e.currentTarget.value)}
          />
        </label>

        <Show when={errorMessage()}>
          {(msg) => (
            <div class="form-error" role="alert">
              {msg()}
            </div>
          )}
        </Show>
      </form>
    </ActionScreen>
  );
};

function humanReadableError(error: AppError): string {
  switch (error.kind) {
    case 'validation':
      return error.message;
    case 'unauthorized':
      return 'Your session has expired or you are not allowed to record this deposit. Please sign in again.';
    case 'not_found':
      return 'That child account no longer exists.';
    case 'network_required':
      return error.message;
    default:
      return `Something went wrong: ${error.message}`;
  }
}
